/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Filter to select time range
 *
 * @author Anton Panasenko
 * @version 1.3.1
 * @since 1.3.0
 */

import { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';

import { icons } from '../../../../assets';
import ICommonTranslation from '../../../../modules/internationalization/types/common';
import { EFormatData } from '../../../../pages/PageDashboard/types/widgetTypes';
import { DropDownFilter } from '../../../common';
import { EInputKind } from '../../../common/Input/Input';
import { TimePicker } from '../../../common/RangeDatePicker/TimePicker';
import { FilterTimeContainerStyled, FilterTimePickerContainerStyled } from './styled/FilterStyled';

export interface IFilterTimeParams {
    filterValue: string;
    startDate: string;
    endDate: string;
}

export interface IFilterTimeProps {
    commonTranslation: ICommonTranslation;
    startTime?: string;
    endTime?: string;
    filterValue: string;
    onChange: (params: IFilterTimeParams) => void;
}

export const getTimeFilterValue = (startTime: Moment, endTime: Moment): string => {
    const start = startTime.format(EFormatData.OnlyHourMinutes);
    const end = endTime.format(EFormatData.OnlyHourMinutes);
    return `${start} ... ${end}`;
};

const FilterTime = (props: IFilterTimeProps) => {
    const [startHour, setStartHour] = useState<number>(0);
    const [startMinute, setStartMinute] = useState<number>(0);

    const [endHour, setEndHour] = useState<number>(23);
    const [endMinute, setEndMinute] = useState<number>(59);

    useEffect(() => {
        if (props.startTime) {
            const startMoment = moment(props.startTime);
            setStartHour(parseInt(startMoment.format(EFormatData.OnlyHour)));
            setStartMinute(parseInt(startMoment.format(EFormatData.OnlyMinutes)));
        }
        if (props.endTime) {
            const endMoment = moment(props.endTime);
            setEndHour(parseInt(endMoment.format(EFormatData.OnlyHour)));
            setEndMinute(parseInt(endMoment.format(EFormatData.OnlyMinutes)));
        }
    }, [props.startTime, props.endTime])

    useEffect(() => {
        if (!isStartTimeBeforeEndTime({})) {
            setEndHour(startHour)
            if (startMinute > endMinute) setEndMinute(startMinute);
        }
    }, [startHour])

    useEffect(() => {
        if (!isStartTimeBeforeEndTime({})) {
            setEndMinute(startMinute);
        }
    }, [startMinute])

    const handleEndHourChange = (value: number) => {
        if (isStartTimeBeforeEndTime({ currentEndHour: value })) {
            setEndHour(value);
        }
    }

    const handleEndMinChange = (value: number) => {
        if (isStartTimeBeforeEndTime({ currentEndMin: value })) {
            setEndMinute(value);
        }
    }

    const isStartTimeBeforeEndTime = ({ currentEndHour, currentEndMin }:
                                          {currentEndHour?: number; currentEndMin?: number}): boolean => {
        const startTime = moment({ hour: startHour, minutes: startMinute });
        const endTime = moment({ hour: currentEndHour ?? endHour, minutes: currentEndMin ?? endMinute });
        return startTime.isBefore(endTime) || startTime.isSame(endTime);
    }

    const handleOkClick = () => {
        const startDate = moment({ hour: startHour, minutes: startMinute });
        const endDate = moment({ hour: endHour, minutes: endMinute });
        props.onChange({
            endDate: endDate.format(EFormatData.FullDataWithTimeZone),
            filterValue: getTimeFilterValue(startDate, endDate),
            startDate: startDate.format(EFormatData.FullDataWithTimeZone),
        })
    }

    return (
        <DropDownFilter
            filterButtonLabel={props.filterValue}
            icon={icons.calendarIcon}
            kind={EInputKind.normal}
            renderInBody={true}
            showCancelBtn={true}
            translation={props.commonTranslation.dropDown}
            widthAuto={true}
            onOkClick={handleOkClick}
        >
            <FilterTimeContainerStyled>
                <FilterTimePickerContainerStyled>
                    <TimePicker
                        hour={startHour}
                        min={startMinute}
                        onChangeHour={setStartHour}
                        onChangeMin={setStartMinute}
                    />
                </FilterTimePickerContainerStyled>

                <FilterTimePickerContainerStyled>
                    <TimePicker
                        hour={endHour}
                        min={endMinute}
                        onChangeHour={handleEndHourChange}
                        onChangeMin={handleEndMinChange}
                    />
                </FilterTimePickerContainerStyled>
            </FilterTimeContainerStyled>
        </DropDownFilter>
    )
}

export default FilterTime;
